import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert } from '../components/ui/Alert'
import { Button } from '../components/ui/Button'
import { Card, CardHeader } from '../components/ui/Card'
import { ConfirmModal } from '../components/ui/ConfirmModal'
import { FeatureToggle } from '../components/ui/FeatureToggle'
import { Input } from '../components/ui/Input'
import { Spinner } from '../components/ui/Spinner'
import { TranslationsEditor, type TranslationFieldDef, type TranslationsAdapter } from '../components/TranslationsEditor'
import {
  FAQ_LIMITS,
  createFaq,
  deleteFaq,
  fetchFaqs,
  reorderFaqs,
  updateFaq,
  type Faq,
} from '../services/helpCenterService'
import {
  fetchContentTranslations,
  regenerateContentTranslation,
  saveContentTranslation,
} from '../services/translationService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'

const TRANSLATION_FIELDS: TranslationFieldDef[] = [
  { key: 'category', label: 'Category' },
  { key: 'question', label: 'Question' },
  { key: 'answer', label: 'Answer', multiline: true, rows: 5 },
]

const textareaClass =
  'w-full rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30'

type Draft = { category: string; question: string; answer: string; isActive: boolean }
const EMPTY: Draft = { category: '', question: '', answer: '', isActive: true }

function FaqTranslations({ id }: { id: number }) {
  const adapter = useMemo<TranslationsAdapter>(
    () => ({
      load: async () => {
        const o = await fetchContentTranslations()
        const found = o.groups.flatMap((g) => g.items).find((i) => i.kind === 'faq' && i.ref === id)
        if (!found) throw new Error('Save the question first, then translations appear here.')
        return found
      },
      save: (locale, values) => saveContentTranslation('faq', id, locale, values),
      regenerate: (locale) => regenerateContentTranslation('faq', id, locale),
    }),
    [id],
  )
  return <TranslationsEditor embedded adapter={adapter} fields={TRANSLATION_FIELDS} />
}

function FaqForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: Draft
  submitLabel: string
  onSubmit: (d: Draft) => Promise<void>
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const valid = draft.question.trim().length > 0 && draft.answer.trim().length > 0

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await onSubmit(draft)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not save'))
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <Input
        label="Question"
        value={draft.question}
        maxLength={FAQ_LIMITS.question}
        placeholder="e.g. Do you deliver on weekends?"
        onChange={(e) => setDraft({ ...draft, question: e.target.value })}
      />
      <div>
        <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="faq-answer">
          Answer
        </label>
        <textarea
          id="faq-answer"
          className={textareaClass}
          rows={5}
          maxLength={FAQ_LIMITS.answer}
          value={draft.answer}
          placeholder="Write the answer shoppers will read."
          onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
        />
      </div>
      <Input
        label="Category (optional)"
        value={draft.category}
        maxLength={FAQ_LIMITS.category}
        placeholder="e.g. Delivery. Questions with the same category are grouped."
        onChange={(e) => setDraft({ ...draft, category: e.target.value })}
      />
      <FeatureToggle
        label="Show on my Help Center"
        description="Turn off to hide this question without deleting it."
        checked={draft.isActive}
        onChange={(isActive) => setDraft({ ...draft, isActive })}
      />
      {error ? <Alert variant="destructive" title="Could not save">{error}</Alert> : null}
      <div className="flex gap-2">
        <Button type="button" onClick={() => void submit()} disabled={!valid || busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

/** Each store writes its own Help Center. With none, shoppers see the platform's standard questions. */
export default function HelpCenter() {
  const [items, setItems] = useState<Faq[] | null>(null)
  const [max, setMax] = useState(60)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [translatingId, setTranslatingId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState<Faq | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await fetchFaqs()
      setItems(res.items)
      setMax(res.max)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load questions'))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function move(index: number, delta: -1 | 1) {
    if (!items) return
    const target = index + delta
    if (target < 0 || target >= items.length) return
    const next = [...items]
    ;[next[index], next[target]] = [next[target], next[index]]
    setItems(next)
    try {
      setItems(await reorderFaqs(next.map((f) => f.id)))
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to reorder questions'))
      void load()
    }
  }

  async function toggleActive(f: Faq) {
    try {
      const saved = await updateFaq(f.id, { isActive: !f.isActive })
      setItems((cur) => cur?.map((x) => (x.id === f.id ? saved : x)) ?? cur)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to save question'))
    }
  }

  if (!items) {
    return (
      <Card>
        <CardHeader title="Help Center" />
        {error ? <Alert variant="destructive" title="Could not load">{error}</Alert> : <Spinner />}
      </Card>
    )
  }

  const atLimit = items.length >= max

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Help Center"
          description="Write the questions your shoppers ask most. They appear on your store's Help Center page and are translated into each language you offer."
          action={
            <Button type="button" size="sm" onClick={() => { setAdding(true); setEditingId(null) }} disabled={adding || atLimit}>
              Add question
            </Button>
          }
        />
        {items.filter((f) => f.isActive).length === 0 ? (
          <Alert variant="info" title="Shoppers currently see our standard answers">
            Until you add and show at least one question, your Help Center displays general questions about orders, returns, payments and accounts. Add your own to replace them.
          </Alert>
        ) : null}
        {atLimit ? <p className="mt-3 text-sm text-muted-foreground">You have reached the limit of {max} questions.</p> : null}
        {error ? <div className="mt-3"><Alert variant="destructive" title="Something went wrong">{error}</Alert></div> : null}
      </Card>

      {adding ? (
        <Card>
          <CardHeader title="New question" />
          <FaqForm
            initial={EMPTY}
            submitLabel="Add question"
            onCancel={() => setAdding(false)}
            onSubmit={async (d) => {
              await createFaq({ ...d, category: d.category.trim() || null })
              setAdding(false)
              await load()
            }}
          />
        </Card>
      ) : null}

      {items.length > 0 ? (
        <Card>
          <ul className="space-y-3">
            {items.map((f, i) => (
              <li key={f.id} className="rounded-2xl border border-border bg-card p-4">
                {editingId === f.id ? (
                  <FaqForm
                    initial={{ category: f.category ?? '', question: f.question, answer: f.answer, isActive: f.isActive }}
                    submitLabel="Save changes"
                    onCancel={() => setEditingId(null)}
                    onSubmit={async (d) => {
                      await updateFaq(f.id, { ...d, category: d.category.trim() || null })
                      setEditingId(null)
                      await load()
                    }}
                  />
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {f.category ? <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{f.category}</p> : null}
                        <p className="font-medium">{f.question}</p>
                        <p className="mt-1 line-clamp-2 whitespace-pre-line text-sm text-muted-foreground">{f.answer}</p>
                        {!f.isActive ? <p className="mt-2 text-xs font-medium text-amber-600">Hidden from shoppers</p> : null}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button type="button" variant="secondary" size="sm" aria-label="Move up" onClick={() => void move(i, -1)} disabled={i === 0}>↑</Button>
                        <Button type="button" variant="secondary" size="sm" aria-label="Move down" onClick={() => void move(i, 1)} disabled={i === items.length - 1}>↓</Button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => { setEditingId(f.id); setAdding(false) }}>Edit</Button>
                      <Button type="button" variant="secondary" size="sm" onClick={() => setTranslatingId(translatingId === f.id ? null : f.id)}>
                        {translatingId === f.id ? 'Hide translations' : 'Translations'}
                      </Button>
                      <Button type="button" variant="secondary" size="sm" onClick={() => void toggleActive(f)}>
                        {f.isActive ? 'Hide' : 'Show'}
                      </Button>
                      <Button type="button" variant="secondary" size="sm" onClick={() => setDeleting(f)}>Delete</Button>
                    </div>
                    {translatingId === f.id ? (
                      <div className="mt-4 border-t border-border pt-4">
                        <FaqTranslations id={f.id} />
                      </div>
                    ) : null}
                  </>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <ConfirmModal
        open={deleting !== null}
        title="Delete this question?"
        description={deleting ? `“${deleting.question}” will be removed from your Help Center in every language.` : undefined}
        confirmLabel="Delete"
        variant="danger"
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return
          try {
            await deleteFaq(deleting.id)
            setDeleting(null)
            await load()
          } catch (e) {
            setDeleting(null)
            setError(getUserFacingErrorMessage(e, 'Failed to delete question'))
          }
        }}
      />
    </div>
  )
}
